/**
 * ErrorBoundary — the app's last line of defence against a white screen.
 *
 * React unmounts the whole tree when a render throws, so one unexpected field
 * in an API payload (a null job title, say) used to leave the user staring at
 * a blank page with nothing to act on. This catches the error, keeps the stack
 * in the console for debugging, and offers a way back.
 */
import React from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import { AlertCircle } from 'lucide-react';

type Props = { children: React.ReactNode };
type State = { error: Error | null };

export class ErrorBoundary extends React.Component<Props, State> {
  public state: State = { error: null };

  public static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  public componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('Unhandled render error', error, info.componentStack);
  }

  public render(): React.ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          px: 3,
          textAlign: 'center',
          bgcolor: 'background.paper',
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: 'error.lighter',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'error.main',
          }}
        >
          <AlertCircle size={28} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Something went wrong
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 420 }}>
          This page could not be displayed. Reloading usually helps; if it keeps happening,
          please let support know what you were doing.
        </Typography>
        <Stack direction="row" spacing={1.5} sx={{ pt: 1 }}>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{
              borderRadius: '9999px',
              bgcolor: '#050508',
              color: '#ffffff',
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { bgcolor: '#1e293b' },
            }}
          >
            Reload
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              window.location.href = '/home';
            }}
            sx={{
              borderRadius: '9999px',
              borderColor: '#cbd5e1',
              color: '#1e293b',
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' },
            }}
          >
            Back to home
          </Button>
        </Stack>
      </Box>
    );
  }
}

export default ErrorBoundary;
